import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { useProgramContext } from '../../../layouts/ProgramLayout';
import { formatLocalYmd, parseLocalDate } from '../_shared/api/nullable';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { useProgram } from '../Programs/hooks';
import { programCompanyHooks } from '../Programs/relationshipHooks';
import { useDeleteSubcontractorReview, useSubcontractorReviews, useUpsertSubcontractorReview } from './hooks';
const stateOptions = [
    {
        value: 'Pending',
        label: 'Pending'
    },
    {
        value: 'Complete',
        label: 'Complete'
    }
];
const columns = [
    {
        key: 'companyName',
        header: 'Company'
    },
    {
        key: 'reviewDate',
        header: 'Review date',
        render: (row)=>row.reviewDate ? formatLocalYmd(row.reviewDate) ?? '—' : '—'
    },
    {
        key: 'score',
        header: 'Score',
        render: (row)=>row.score.toFixed(2)
    },
    {
        key: 'state',
        header: 'State'
    },
    {
        key: 'reviewedBy',
        header: 'Reviewed by'
    }
];
export function SubcontractorReviewsTab({ programId }) {
    const { canEdit } = useProgramContext();
    const { data, isLoading } = useSubcontractorReviews(programId);
    const upsert = useUpsertSubcontractorReview();
    const remove = useDeleteSubcontractorReview();
    const assignmentsQuery = programCompanyHooks.useList({
        programId
    });
    // Delete requires the quality write area on the RPC (same gate as Risk/Issue/
    // Deviation deletes) — resolved server-side rather than a client-only check.
    const canManageQuality = useProgram(programId).data?.canManageQuality ?? false;
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const assignments = assignmentsQuery.data?.items ?? [];
    const fields = useMemo(()=>[
            {
                kind: 'select',
                name: 'companyAssignmentId',
                label: 'Subcontractor (company assignment)',
                required: true,
                options: assignments.map((a)=>({
                        value: a.id,
                        label: a.companyName
                    })),
                helpText: 'Pick from the program’s Companies tab assignments.'
            },
            {
                kind: 'date',
                name: 'reviewDate',
                label: 'Review date',
                required: true
            },
            {
                kind: 'date',
                name: 'dueDate',
                label: 'Due date',
                required: true
            },
            {
                kind: 'number',
                name: 'score',
                label: 'Score (0–5)',
                min: 0,
                max: 5,
                step: 0.01
            },
            {
                kind: 'textarea',
                name: 'feedback',
                label: 'Feedback',
                rows: 3
            },
            {
                kind: 'select',
                name: 'state',
                label: 'State',
                options: stateOptions
            },
            {
                kind: 'text',
                name: 'reviewedBy',
                label: 'Reviewed by',
                maxLength: 200
            }
        ], [
        assignments
    ]);
    const handleSubmit = async (values)=>{
        await upsert.mutateAsync({
            id: editing?.id ?? '',
            programId,
            companyAssignmentId: values.companyAssignmentId,
            reviewDate: parseLocalDate(values.reviewDate),
            dueDate: parseLocalDate(values.dueDate),
            score: Number(values.score) || 0,
            feedback: values.feedback ?? '',
            state: values.state ?? 'Pending',
            reviewedBy: values.reviewedBy ?? ''
        });
        setFormOpen(false);
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>{
                        setEditing(null);
                        setFormOpen(true);
                    },
                    disabled: !canEdit || assignments.length === 0,
                    "data-testid": Testids.deliveryRisk.subReviewCreate,
                    children: "New Review"
                })
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: data?.items ?? [],
                columns: columns,
                testid: Testids.deliveryRisk.subReviewsTable,
                rowTestid: Testids.deliveryRisk.subReviewRow,
                rowKey: (r)=>r.id,
                isLoading: isLoading,
                hideRowActions: !canEdit,
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>{
                                    setEditing(row);
                                    setFormOpen(true);
                                },
                                "data-testid": Testids.deliveryRisk.subReviewEdit,
                                children: "Edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                disabled: !canManageQuality,
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryRisk.subReviewDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: editing ? 'Edit Subcontractor Review' : 'New Subcontractor Review',
                fields: fields,
                defaultValues: {
                    id: editing?.id ?? '',
                    companyAssignmentId: editing?.companyAssignmentId ?? '',
                    reviewDate: editing?.reviewDate ? formatLocalYmd(editing.reviewDate) ?? '' : '',
                    dueDate: editing?.dueDate ? formatLocalYmd(editing.dueDate) ?? '' : '',
                    score: editing?.score ?? 0,
                    feedback: editing?.feedback ?? '',
                    state: editing?.state ?? 'Pending',
                    reviewedBy: editing?.reviewedBy ?? ''
                },
                onSubmit: handleSubmit,
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Subcontractor Review",
                description: deleting ? `Delete the review for "${deleting.companyName}"? It will be deactivated and hidden from lists; an administrator can restore it.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            })
        ]
    });
}
